/**
 * SetupGuidePage — 生产环境配置指引（默认主页）
 * 登录后的落地页：以「配置清单」形式引导上线（域名/MX、邮箱账号、SPF、DKIM、DMARC、TLS、发信测试）。
 * 每项显示完成状态（✓ 已完成 / ○ 待配置），带「去配置」入口；全部自动可检项完成后提示系统就绪。
 */
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Card,
  Badge,
  Spinner,
  MessageBar,
  MessageBarBody,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from '@fluentui/react-components'
import {
  CheckmarkCircle20Regular,
  Circle20Regular,
  Rocket20Regular,
  Copy20Regular,
} from '@fluentui/react-icons'
import { domainsApi } from '@/api/domains'
import { usersApi } from '@/api/users'
import { dkimApi } from '@/api/dkim'
import { tlsApi } from '@/api/tls'
import { serverConfigApi } from '@/api/serverConfig'
import { ROUTES } from '@/routes'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  hero: {
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  heroTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
  },
  heroDesc: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
    lineHeight: '20px',
  },
  stepCard: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  stepRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  stepIconDone: {
    color: tokens.colorStatusSuccessForeground1,
    flexShrink: 0,
    marginTop: '2px',
  },
  stepIconTodo: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
    marginTop: '2px',
  },
  stepMain: {
    flex: 1,
    minWidth: 0,
  },
  stepHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  stepTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
  },
  stepDesc: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginTop: '4px',
    lineHeight: '18px',
  },
  record: {
    marginTop: '8px',
    background: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '8px 10px',
    fontFamily: 'Consolas, monospace',
    fontSize: tokens.fontSizeBase200,
    wordBreak: 'break-all',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
  copyBtn: {
    flexShrink: 0,
    minWidth: '80px',
  },
  ready: {
    padding: '16px',
  },
  dnsSection: {
    marginTop: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  dnsToolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
  dnsHint: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    lineHeight: '18px',
  },
  dnsTable: {
    fontSize: tokens.fontSizeBase200,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
  },
  mono: {
    fontFamily: 'Consolas, monospace',
    wordBreak: 'break-all',
  },
  missing: {
    color: tokens.colorNeutralForeground4,
    fontStyle: 'italic',
  },
  // 第三方客户端配置面板
  clientCard: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  clientHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
  },
  clientDesc: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    lineHeight: '18px',
  },
  clientSubTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    marginTop: '4px',
  },
  protoTable: {
    fontSize: tokens.fontSizeBase200,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
  },
  protoMono: {
    fontFamily: 'Consolas, monospace',
    wordBreak: 'break-all',
  },
  steps: {
    margin: '4px 0 0',
    paddingLeft: '20px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    lineHeight: '22px',
  },
  kbd: {
    fontFamily: 'Consolas, monospace',
    background: tokens.colorNeutralBackground3,
    borderRadius: '3px',
    padding: '0 4px',
    fontSize: tokens.fontSizeBase200,
  },
})

function useCopy() {
  return async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      const ta = document.createElement('textarea')
      ta.value = text
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
    }
  }
}

interface DnsRecord {
  host: string // 主机记录（不含域名后缀，如 mail / @ / _dmarc）
  type: 'A' | 'MX' | 'TXT'
  value: string // 记录值（IP / 主机名 / TXT 策略串）
  priority?: number // MX 优先级
  ttl?: number
}

interface Step {
  key: string
  title: string
  desc: string
  done: boolean
  route?: string
  actionLabel?: string
  records?: { label: string; value: string; copyable?: boolean }[]
  dnsRecords?: DnsRecord[]
  dnsMissingIp?: boolean
  dnsMissingDomain?: boolean
}

export default function SetupGuidePage() {
  const styles = useStyles()
  const navigate = useNavigate()
  const copy = useCopy()

  const domainsQ = useQuery({ queryKey: ['domains'], queryFn: domainsApi.list })
  const usersQ = useQuery({ queryKey: ['users-guide'], queryFn: () => usersApi.list(1, 100) })
  const dkimQ = useQuery({ queryKey: ['dkim'], queryFn: dkimApi.list })
  const tlsQ = useQuery({ queryKey: ['tls-config'], queryFn: tlsApi.getConfig })
  const serverQ = useQuery({ queryKey: ['server-config'], queryFn: serverConfigApi.get })

  const loading = domainsQ.isLoading || usersQ.isLoading || dkimQ.isLoading || tlsQ.isLoading || serverQ.isLoading

  const steps = useMemo<Step[]>(() => {
    const domains = domainsQ.data ?? []
    const users = usersQ.data ?? []
    const dkim = dkimQ.data ?? []
    const tls = tlsQ.data
    const server = serverQ.data
    const primaryDomain = domains[0]?.name

    const hasCert = !!tls?.cert?.present && !!tls?.cert?.valid
    const hasUser = users.some((u) => !u.is_admin)
    // 所有收件域均已配置出站 HELO 主机名（与 A 记录 / PTR 一致）
    const allHelo = domains.length > 0 && domains.every((d) => d.heloDomain)

    // 多条 MX 记录：优先使用 server-config 中配置的 mx_records，否则回退默认 mail.域名
    const mxList = server?.mx_records?.length
      ? server.mx_records
      : primaryDomain
        ? [{ priority: 10, host: `mail.${primaryDomain}` }]
        : []

    // 配置收件域：把 A / MX 记录结构化为逐字段（主机记录 / 类型 / 记录值 / 优先级），便于在服务商面板逐项填写
    const stripDomain = (h: string) =>
      primaryDomain && h.endsWith(`.${primaryDomain}`)
        ? h.slice(0, -(primaryDomain.length + 1))
        : h
    const dnsMissingIp = !server?.server_ip
    const dnsRecords: DnsRecord[] = []
    if (primaryDomain) {
      const aHost = mxList[0]?.host || `mail.${primaryDomain}`
      dnsRecords.push({
        host: stripDomain(aHost) || 'mail',
        type: 'A',
        value: server?.server_ip ?? '',
        ttl: 600,
      })
      mxList.forEach((m) => {
        dnsRecords.push({
          host: '@',
          type: 'MX',
          value: m.host,
          priority: m.priority,
          ttl: 600,
        })
      })
    }

    return [
      {
        key: 'domain',
        title: '配置收件域',
        desc: primaryDomain
          ? `已添加主域 ${primaryDomain}。请在域名服务商处将 A 记录指向本服务器，MX 记录指向 A 记录主机名。`
          : '添加你的真实域名，并在域名服务商处配置 A / MX 记录使其可收信。',
        done: domains.length > 0,
        route: ROUTES.DOMAINS,
        actionLabel: '去配置',
        dnsRecords: dnsRecords.length ? dnsRecords : undefined,
        dnsMissingIp,
      },
      {
        key: 'user',
        title: '创建邮箱账号',
        desc: '创建首个邮箱账号（如需发信，SMTP 认证身份）。',
        done: hasUser,
        route: ROUTES.DOMAINS,
        actionLabel: '去配置',
      },
      {
        key: 'spf',
        title: '配置 SPF 记录',
        desc: '在域名服务商处为发信域添加 SPF TXT 记录，声明哪些主机可代本域发信，防止被判定为垃圾邮件。',
        done: false,
        dnsRecords: [{
          host: '@',
          type: 'TXT',
          value: 'v=spf1 mx ~all',
          ttl: 600,
        }],
      },
      {
        key: 'dkim',
        title: '配置 DKIM 签名',
        desc: dkim.length > 0
          ? `已生成 ${dkim.length} 个 DKIM 密钥，请在域名服务商发布以下 TXT 记录（每条对应一个域的签名公钥）。`
          : '生成 DKIM 密钥并为域名发布 TXT 记录，增加发信信任度。',
        done: dkim.length > 0,
        route: ROUTES.DOMAINS,
        actionLabel: '去配置',
        dnsRecords: dkim.length
          ? dkim.map((d) => {
              const host =
                d.dns_name && d.domain && d.dns_name.endsWith(`.${d.domain}`)
                  ? d.dns_name.slice(0, -(d.domain.length + 1))
                  : d.dns_name || `${d.selector}._domainkey`
              return {
                host,
                type: 'TXT' as const,
                value: d.dns_record ?? '',
                ttl: 600,
              }
            })
          : undefined,
      },
      {
        key: 'dmarc',
        title: '配置 DMARC 记录',
        desc: '添加 DMARC TXT 记录，告诉接收方如何处理未通过 SPF/DKIM 的邮件。',
        done: false,
        dnsRecords: [{
          host: '_dmarc',
          type: 'TXT',
          value: primaryDomain
            ? `v=DMARC1; p=none; rua=mailto:postmaster@${primaryDomain}`
            : 'v=DMARC1; p=none; rua=mailto:postmaster@yourdomain.com',
          ttl: 600,
        }],
        dnsMissingDomain: !primaryDomain,
      },
      {
        key: 'helo',
        title: '配置服务器主机名',
        desc: domains.length > 0
          ? '为每个发信域配置对外宣告的 HELO 主机名（如 mail.500p.tech），需与该域 A 记录、服务器公网 IP 的反向解析（PTR）三者一致，否则外发邮件易被接收方判为垃圾邮件。'
          : '添加收件域后，为每个发信域配置对外宣告的 HELO 主机名（需与 A 记录、PTR 一致），避免外发邮件被判垃圾。',
        done: allHelo,
        route: ROUTES.DOMAINS,
        actionLabel: '去配置',
      },
      {
        key: 'tls',
        title: '配置 TLS 证书',
        desc: hasCert
          ? '已检测到有效证书并启用 TLS（STARTTLS / SMTPS / IMAPS / POP3S）。'
          : '上传或签发 TLS 证书（如 certbot），启用加密收发。',
        done: hasCert,
        route: ROUTES.DOMAINS,
        actionLabel: '去配置',
      },
      {
        key: 'test',
        title: '发信测试',
        desc: '向外部邮箱发送一封测试邮件，验证整条发信链路（SPF/DKIM 生效情况）。',
        done: false,
        route: ROUTES.MAIL_TEST,
        actionLabel: '去写信',
      },
    ]
  }, [domainsQ.data, usersQ.data, dkimQ.data, tlsQ.data, serverQ.data])

  const autoSteps = ['domain', 'user', 'dkim', 'helo', 'tls']
  const allCoreDone = autoSteps.every((k) => steps.find((s) => s.key === k)?.done)

  // 第三方客户端（Foxmail 等）连接用的服务器地址：优先取 MX 主机名，否则回退 mail.主域名
  const primaryDomainName = domainsQ.data?.[0]?.name
  const serverHost = useMemo(() => {
    if (serverQ.data?.mx_records?.length) return serverQ.data.mx_records[0].host
    if (primaryDomainName) return `mail.${primaryDomainName}`
    return ''
  }, [serverQ.data, primaryDomainName])

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
        <Spinner label="加载配置进度..." />
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <Card className={styles.hero}>
        <div className={styles.heroTitle}>
          <Rocket20Regular /> 上线配置指引
          {allCoreDone ? (
            <Badge appearance="filled" color="success">
              已就绪
            </Badge>
          ) : (
            <Badge appearance="filled">进行中</Badge>
          )}
        </div>
        <Text className={styles.heroDesc}>
          按以下步骤配置你的邮件服务器即可上线。SPF / DKIM / DMARC 记录需在你的域名服务商控制台添加。完成后即可正常收发邮件。
        </Text>
      </Card>

      {allCoreDone && (
        <MessageBar intent="success">
          <MessageBarBody>
            核心配置已完成，系统已就绪！
            <Button
              appearance="primary"
              size="small"
              style={{ marginLeft: '12px' }}
              onClick={() => navigate(ROUTES.STATS)}
            >
              进入统计概览
            </Button>
          </MessageBarBody>
        </MessageBar>
      )}

      {steps.map((s) => (
        <Card key={s.key} className={styles.stepCard}>
          <div className={styles.stepRow}>
            {s.done ? (
              <CheckmarkCircle20Regular className={styles.stepIconDone} />
            ) : (
              <Circle20Regular className={styles.stepIconTodo} />
            )}
            <div className={styles.stepMain}>
              <div className={styles.stepHeader}>
                <Text className={styles.stepTitle}>{s.title}</Text>
                <Badge appearance="tint" color={s.done ? 'success' : 'severe'}>
                  {s.done ? '已完成' : '待配置'}
                </Badge>
              </div>
              <Text block className={styles.stepDesc}>
                {s.desc}
              </Text>
              {s.dnsRecords ? (
                <>
                  {s.dnsMissingIp && (
                    <MessageBar intent="warning" style={{ marginTop: '8px' }}>
                      <MessageBarBody>
                        尚未配置服务器公网 IP，A 记录值暂无法生成。请前往「收件域 → SMTP/收信设置」填写。
                        <Button
                          appearance="primary"
                          size="small"
                          style={{ marginLeft: '12px' }}
                          onClick={() => navigate(ROUTES.DOMAINS)}
                        >
                          去设置
                        </Button>
                      </MessageBarBody>
                    </MessageBar>
                  )}
                  {s.dnsMissingDomain && (
                    <MessageBar intent="warning" style={{ marginTop: '8px' }}>
                      <MessageBarBody>
                        尚未添加主域名，DMARC 报告邮箱暂使用占位域名。请先在「收件域」添加你的真实域名。
                        <Button
                          appearance="primary"
                          size="small"
                          style={{ marginLeft: '12px' }}
                          onClick={() => navigate(ROUTES.DOMAINS)}
                        >
                          去设置
                        </Button>
                      </MessageBarBody>
                    </MessageBar>
                  )}
                  <DnsRecordsTable records={s.dnsRecords} onCopy={copy} />
                </>
              ) : (
                s.records?.map((r, i) => (
                  <div className={styles.record} key={i}>
                    <span>
                      {r.label}: {r.value}
                    </span>
                    <Button
                      className={styles.copyBtn}
                      size="small"
                      icon={<Copy20Regular />}
                      disabled={r.copyable === false}
                      onClick={() => copy(r.value)}
                    >
                      {r.copyable === false ? '待配置' : '复制'}
                    </Button>
                  </div>
                ))
              )}
            </div>
            {s.route && !s.done && (
              <Button appearance="primary" onClick={() => navigate(s.route!)}>
                {s.actionLabel ?? '去配置'}
              </Button>
            )}
          </div>
        </Card>
      ))}

      <ClientSetupPanel serverHost={serverHost} domain={primaryDomainName} />
    </div>
  )
}

function ClientSetupPanel({ serverHost, domain }: { serverHost: string; domain?: string }) {
  const styles = useStyles()
  const navigate = useNavigate()
  const rows: { proto: string; port: string; tls: string }[] = [
    { proto: 'IMAP（收信）', port: '993', tls: 'SSL/TLS（推荐）' },
    { proto: 'IMAP（收信）', port: '143', tls: 'STARTTLS' },
    { proto: 'POP3（收信）', port: '995', tls: 'SSL/TLS' },
    { proto: 'POP3（收信）', port: '110', tls: 'STARTTLS' },
    { proto: 'SMTP（发信）', port: '465', tls: 'SSL/TLS（推荐）' },
    { proto: 'SMTP（发信）', port: '25', tls: 'STARTTLS（服务器间传输，客户端不推荐）' },
  ]
  return (
    <Card className={styles.clientCard}>
      <div className={styles.clientHeader}>
        <Rocket20Regular /> 用 Foxmail / Outlook 等客户端收发邮件
      </div>
      <Text block className={styles.clientDesc}>
        核心配置完成后，可用任意标准邮件客户端（Foxmail、Outlook、Thunderbird、手机自带邮件 App）通过 IMAP/POP3 收信、SMTP 发信。下面以 Foxmail 为例说明连接参数。
      </Text>

      <Text block className={styles.clientSubTitle}>
        ① 服务器地址与端口
      </Text>
      {serverHost ? (
        <Table className={styles.protoTable} size="small">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>协议</TableHeaderCell>
              <TableHeaderCell>服务器地址</TableHeaderCell>
              <TableHeaderCell>端口</TableHeaderCell>
              <TableHeaderCell>加密方式</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={i}>
                <TableCell>{r.proto}</TableCell>
                <TableCell className={styles.protoMono}>{serverHost}</TableCell>
                <TableCell className={styles.protoMono}>{r.port}</TableCell>
                <TableCell>{r.tls}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <MessageBar intent="warning">
          <MessageBarBody>
            尚未添加主域名，服务器地址暂无法生成。请先在「收件域」添加你的真实域名。
            <Button
              appearance="primary"
              size="small"
              style={{ marginLeft: '12px' }}
              onClick={() => navigate(ROUTES.DOMAINS)}
            >
              去设置
            </Button>
          </MessageBarBody>
        </MessageBar>
      )}

      <Text block className={styles.clientSubTitle}>
        ② 账号信息
      </Text>
      <Text block className={styles.clientDesc}>
        用户名：<span className={styles.protoMono}>完整邮箱地址</span>（如 {domain ? `user@${domain}` : 'user@yourdomain.com'}）；
        密码：<span className={styles.protoMono}>该邮箱的登录密码</span>；认证方式：普通密码（AUTH LOGIN / PLAIN）。
      </Text>

      <Text block className={styles.clientSubTitle}>
        ③ Foxmail 配置步骤
      </Text>
      <ol className={styles.steps}>
        <li>新建账号 → 选择「其他邮箱」或「手动配置」。</li>
        <li>邮箱类型选 <span className={styles.kbd}>IMAP</span>（推荐，多端同步）或 <span className={styles.kbd}>POP3</span>。</li>
        <li>收信服务器填 <span className={styles.protoMono}>{serverHost || 'mail.你的域名'}</span>，端口 <span className={styles.kbd}>993</span>（SSL/TLS）。</li>
        <li>发信服务器填 <span className={styles.protoMono}>{serverHost || 'mail.你的域名'}</span>，端口 <span className={styles.kbd}>465</span>（SSL/TLS）。</li>
        <li>用户名填完整邮箱，密码填邮箱登录密码，保存即可收发。</li>
        <li>若客户端提示证书警告，请将「收件域 → TLS 安全」处的证书对齐为该服务器地址（即 MX 主机名），或勾选「信任此证书」继续。</li>
      </ol>
    </Card>
  )
}

function DnsRecordsTable({
  records,
  onCopy,
}: {
  records: DnsRecord[]
  onCopy: (text: string) => void
}) {
  const styles = useStyles()
  const copyAll = () => {
    const lines = records.map((r) => {
      const base = `${r.host}  ${r.ttl ?? 600}  IN  ${r.type}`
      if (r.type === 'MX') return `${base}  ${r.priority}  ${r.value}.`
      if (r.type === 'TXT') return `${base}  "${r.value}"`
      return `${base}  ${r.value}`
    })
    onCopy(lines.join('\n'))
  }
  return (
    <div className={styles.dnsSection}>
      <div className={styles.dnsToolbar}>
        <Text className={styles.dnsHint}>
          在域名服务商 DNS 控制台按以下字段逐条添加（主机记录无需带域名后缀）：
        </Text>
        <Button size="small" icon={<Copy20Regular />} onClick={copyAll}>
          复制全部
        </Button>
      </div>
      <Table className={styles.dnsTable} size="small">
        <TableHeader>
          <TableRow>
            <TableHeaderCell>主机记录</TableHeaderCell>
            <TableHeaderCell>类型</TableHeaderCell>
            <TableHeaderCell>记录值</TableHeaderCell>
            <TableHeaderCell>优先级</TableHeaderCell>
            <TableHeaderCell>TTL</TableHeaderCell>
            <TableHeaderCell></TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {records.map((r, i) => (
            <TableRow key={i}>
              <TableCell>
                <code className={styles.mono}>{r.host}</code>
              </TableCell>
              <TableCell>
                <Badge appearance="outline">{r.type}</Badge>
              </TableCell>
              <TableCell className={styles.mono}>
                {r.value ? (
                  r.value
                ) : (
                  <span className={styles.missing}>待填写服务器 IP</span>
                )}
              </TableCell>
              <TableCell>{r.priority != null ? r.priority : '—'}</TableCell>
              <TableCell>{r.ttl ?? 600}</TableCell>
              <TableCell>
                <Button
                  size="small"
                  icon={<Copy20Regular />}
                  disabled={!r.value}
                  onClick={() =>
                    onCopy(
                      r.type === 'A'
                        ? `${r.host}  ${r.ttl ?? 600}  IN  A  ${r.value}`
                        : r.type === 'MX'
                          ? `${r.host}  ${r.ttl ?? 600}  IN  MX  ${r.priority}  ${r.value}.`
                          : `${r.host}  ${r.ttl ?? 600}  IN  TXT  "${r.value}"`,
                    )
                  }
                >
                  复制
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Text className={styles.dnsHint}>
        {records.some((r) => r.type === 'MX')
          ? '提示：MX 记录「记录值」填 A 记录的主机名（如 mail.500p.tech），优先级数字越小优先级越高。'
          : '提示：TXT 记录「记录值」为策略串；部分服务商需要在值两侧加英文双引号。'}
      </Text>
    </div>
  )
}